import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Button, Dropdown } from '@carbon/react';
import {
  ChevronRight,
  ChevronDown,
  Document,
  ExpandAll,
  CollapseAll,
  Calendar,
  EventSchedule,
  Catalog,
  Time,
  DocumentMultiple_01 as DocumentMultiple01,
  WarningHex,
} from '@carbon/react/icons';
import classNames from 'classnames';
import { LinkWithHover } from 'components/hover-card-resource';
import {
  OwnershipGraph,
  OwnershipNode,
  ConveyanceEdge,
  DocumentRecord,
  DocumentDetailMap,
} from './types';
import {
  getEdgeTypeName,
  getEdgeTypeLabel,
  formatInterestFraction,
  getNodeNRI,
  computeOperationExpression,
  formatDepthBand,
} from './data-helpers';
import {
  SeveranceVisualization,
  type SeveranceGeography,
} from './severance-visualization';
import { FloatingTooltip } from 'components/floating-tooltip';
import { format2 } from 'utils/number';
import styles from './conveyance-runsheet.module.scss';
import tableStyles from './ownership-tanstack-table.module.scss';

// * Entity link with hover card preview.
// @returns The rendered entity link JSX element.
export function EntityLink({
  entityId,
  children,
}: {
  entityId: number;
  children: React.ReactNode;
}) {
  const href = `/entities/${entityId}`;
  return (
    <LinkWithHover kind="entity" href={href}>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </a>
    </LinkWithHover>
  );
}

// Interest column definitions — matches the ownership entries table
const INTEREST_COLUMNS = [
  { key: 'mineral', label: 'Mineral', width: '60px' },
  { key: 'surface', label: 'Surface', width: '60px' },
  { key: 'executive', label: 'Exec', width: '52px' },
  { key: 'royalty', label: 'Royalty', width: '60px' },
  { key: 'bonus', label: 'Bonus', width: '52px' },
  { key: 'delay', label: 'Delay', width: '52px' },
  { key: 'working', label: 'WI', width: '52px' },
  { key: 'orri', label: 'ORRI', width: '52px' },
] as const;

const EDGE_DETAIL_EXPAND_LABEL = 'Show pre & post operation ownership';
const EDGE_DETAIL_COLLAPSE_LABEL = 'Hide ownership details';

const SORT_OPTIONS = [
  { id: 'effective_newest', text: 'Effective Date (Newest)' },
  { id: 'effective_oldest', text: 'Effective Date (Oldest)' },
  { id: 'recorded_newest', text: 'Recorded Date (Newest)' },
  { id: 'recorded_oldest', text: 'Recorded Date (Oldest)' },
] as const;

const getInterestFraction = (
  node: OwnershipNode,
  interestType: string
): string | null => {
  for (const interest of node.interests) {
    if (interest.interestType === interestType) {
      return formatInterestFraction(interest);
    }
  }
  return null;
};

// =============================================================================
// Types
// =============================================================================

interface RunsheetTransaction {
  transactionId: number;
  edges: ConveyanceEdge[];
  validCount: number;
  invalidCount: number;
  conveyors: { name: string; entityId: number }[];
  conveyees: { name: string; entityId: number }[];
  effectiveDate: string;
  conveyedEstates: string[];
}

interface RunsheetDocument {
  documentId: number;
  documentTitle: string | null;
  documentType: string | null;
  county: string | null;
  state: string | null;
  transactionDate: string | null;
  effectiveDate: string;
  instrumentDate: string | null;
  fileDate: string | null;
  recordingDate: string | null;
  transactions: RunsheetTransaction[];
  validCount: number;
  invalidCount: number;
  grantors: { name: string; entityId: number }[];
  grantees: { name: string; entityId: number }[];
  areas: { name: string; depth: string | null }[];
  totalOps: number;
}

type SortOrder =
  | 'effective_newest'
  | 'effective_oldest'
  | 'recorded_newest'
  | 'recorded_oldest';

// =============================================================================
// Props
// =============================================================================

interface ConveyanceRunsheetProps {
  ownershipGraph: OwnershipGraph;
  documents: Record<string, DocumentRecord>;
  documentDetails?: DocumentDetailMap;
  totalDocumentCount?: number;
  selectedEdgeIds: Set<string>;
  selectedDocumentId: number | null;
  selectedTransactionId: number | null;
  selectedNodeId: string | null;
  onEdgeClick: (edgeId: string, documentId: number) => void;
  onNodeClick?: (nodeId: string) => void;
  onDocumentClick?: (documentId: number) => void;
  onTransactionClick?: (transactionId: number | null) => void;
  onEnterDocReview?: (documentId?: number) => void;
  // Start of the timeline window (YYYY-MM-DD). null = from beginning.
  startDate?: string | null;
  // End of the timeline window (YYYY-MM-DD). null = to latest.
  endDate?: string | null;
  // Map geographies for severance visualizations
  geographies?: SeveranceGeography[];
  // Callback when a property name is clicked — opens the asset preview
  onAssetClick?: (data: {
    propertyName: string;
    nodeId: string;
    anchorRect?: DOMRect;
    pin?: boolean;
  }) => void;
  onAssetLeave?: () => void;
}

// =============================================================================
// Helpers
// =============================================================================

export function groupEdgesByDocument(
  graph: OwnershipGraph,
  documents: Record<string, DocumentRecord>,
  documentDetails?: DocumentDetailMap
): RunsheetDocument[] {
  // Two-level grouping: document_id -> transaction_id -> edges[]
  // Skip edges with null document_id (E5 Consolidation, data gaps) — they aren't
  // tied to any recorded document and shouldn't appear in the runsheet.
  const docGroups = new Map<number, Map<number, ConveyanceEdge[]>>();
  for (const edge of Object.values(graph.edges)) {
    if (edge.documentId === null) continue;
    const docId = edge.documentId;
    const txId = edge.transactionId ?? 0;

    if (!docGroups.has(docId)) {
      docGroups.set(docId, new Map());
    }
    const txGroups = docGroups.get(docId)!;
    if (!txGroups.has(txId)) {
      txGroups.set(txId, []);
    }
    txGroups.get(txId)!.push(edge);
  }

  const result: RunsheetDocument[] = [];

  for (const [documentId, txGroups] of docGroups) {
    const grantorMap = new Map<string, number>(); // name → entityId
    const granteeMap = new Map<string, number>();
    // key: name + depth label → area entry (same area at different depths shows once per depth)
    const areaMap = new Map<string, { name: string; depth: string | null }>();
    let totalValid = 0;
    let totalInvalid = 0;

    const runsheetTransactions: RunsheetTransaction[] = [];

    // Resolve document metadata once from the documents hash
    const docRecord = documents[String(documentId)];

    for (const [transactionId, edges] of txGroups) {
      let txValid = 0;
      let txInvalid = 0;
      const txConveyorMap = new Map<string, number>();
      const txConveyeeMap = new Map<string, number>();
      const txEstateSet = new Set<string>();
      for (const edge of edges) {
        const fromNode =
          edge.fromNodeId !== null ? graph.nodes[edge.fromNodeId] : undefined;
        const toNode =
          edge.toNodeId !== null ? graph.nodes[edge.toNodeId] : undefined;
        if (fromNode) {
          grantorMap.set(fromNode.entity.entityName, fromNode.entity.entityId);
          txConveyorMap.set(
            fromNode.entity.entityName,
            fromNode.entity.entityId
          );
        }
        if (toNode) {
          granteeMap.set(toNode.entity.entityName, toNode.entity.entityId);
          txConveyeeMap.set(toNode.entity.entityName, toNode.entity.entityId);
          if (toNode.property.volume.geometryName) {
            const name = toNode.property.volume.geometryName;
            const depth = formatDepthBand(toNode.property.volume.depthRange);
            areaMap.set(`${name}::${depth}`, { name, depth });
          }
          const estate = toNode.asset?.estateType;
          if (estate === 'unified') {
            txEstateSet.add('Surface');
            txEstateSet.add('Mineral');
          } else if (estate) {
            txEstateSet.add(estate.charAt(0).toUpperCase() + estate.slice(1));
          }
        }
        if (edge.isValid) txValid++;
        else txInvalid++;
      }

      totalValid += txValid;
      totalInvalid += txInvalid;

      // When edge nodes are null, fall back to interpretation data for
      // transaction-level conveyors, conveyees, and estates
      if (txConveyorMap.size === 0 || txConveyeeMap.size === 0) {
        const docDetail = documentDetails?.[String(documentId)];
        if (docDetail) {
          for (const interp of docDetail.interpretations) {
            const tx = (interp.transactions ?? []).find(
              (t) => t.id === transactionId
            );
            if (tx) {
              if (txConveyorMap.size === 0) {
                for (const g of tx.grantors) {
                  if (
                    g.name &&
                    g.entityId !== null &&
                    g.entityId !== undefined
                  ) {
                    txConveyorMap.set(g.name, g.entityId);
                    grantorMap.set(g.name, g.entityId);
                  }
                }
              }
              if (txConveyeeMap.size === 0) {
                for (const c of tx.conveyances) {
                  if (c.grantee_name) {
                    txConveyeeMap.set(c.grantee_name, c.grantee_id);
                    granteeMap.set(c.grantee_name, c.grantee_id);
                  }
                }
              }
              if (txEstateSet.size === 0) {
                txEstateSet.add('Mineral');
              }
              break;
            }
          }
        }
      }

      runsheetTransactions.push({
        transactionId,
        edges,
        validCount: txValid,
        invalidCount: txInvalid,
        conveyors: Array.from(txConveyorMap.entries()).map(
          ([name, entityId]) => ({ name, entityId })
        ),
        conveyees: Array.from(txConveyeeMap.entries()).map(
          ([name, entityId]) => ({ name, entityId })
        ),
        effectiveDate: docRecord?.effectiveDate ?? '',
        conveyedEstates: Array.from(txEstateSet),
      });
    }

    // When edge nodes are null (invalid operations), fall back to interpretation
    // data for grantors, grantees, and areas so the document row isn't empty.
    if (grantorMap.size === 0 || granteeMap.size === 0 || areaMap.size === 0) {
      const docDetail = documentDetails?.[String(documentId)];
      if (docDetail) {
        for (const interp of docDetail.interpretations) {
          if (grantorMap.size === 0) {
            for (const tx of interp.transactions ?? []) {
              for (const g of tx.grantors ?? []) {
                if (g.name && g.entityId !== null && g.entityId !== undefined)
                  grantorMap.set(g.name, g.entityId);
              }
            }
          }
          if (granteeMap.size === 0) {
            for (const tx of interp.transactions ?? []) {
              for (const c of tx.conveyances ?? []) {
                if (c.grantee_name)
                  granteeMap.set(c.grantee_name, c.grantee_id);
              }
            }
          }
          if (areaMap.size === 0) {
            for (const loc of interp.location_references ?? []) {
              if (loc.reference_location_name) {
                areaMap.set(`${loc.reference_location_name}::`, {
                  name: loc.reference_location_name,
                  depth: null,
                });
              }
            }
          }
        }
      }
    }

    result.push({
      documentId,
      documentTitle: docRecord?.documentTitle ?? null,
      documentType: docRecord?.documentType ?? null,
      county: docRecord?.county ?? null,
      state: docRecord?.state ?? null,
      transactionDate: null,
      effectiveDate: docRecord?.effectiveDate ?? '',
      instrumentDate: docRecord?.instrumentDate ?? null,
      fileDate: docRecord?.fileDate ?? null,
      recordingDate: docRecord?.recordingDate ?? null,
      transactions: runsheetTransactions,
      validCount: totalValid,
      invalidCount: totalInvalid,
      grantors: Array.from(grantorMap.entries()).map(([name, entityId]) => ({
        name,
        entityId,
      })),
      grantees: Array.from(granteeMap.entries()).map(([name, entityId]) => ({
        name,
        entityId,
      })),
      areas: Array.from(areaMap.values()),
      totalOps: countLogicalOps(runsheetTransactions.flatMap((t) => t.edges)),
    });
  }

  return result;
}

function formatShortDate(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

// =============================================================================
// Edge Row grouping — collapses Consolidation edges into a single logical row
// =============================================================================

type EdgeRow =
  | { kind: 'single'; edge: ConveyanceEdge }
  | {
      kind: 'paired';
      conveyedEdge: ConveyanceEdge;
      retainedEdge: ConveyanceEdge;
      groupKey: string;
    }
  | {
      kind: 'consolidation';
      edges: ConveyanceEdge[];
      toNodeId: string;
      groupKey: string;
    };

// * Build display rows from conveyance edges, grouping consolidations and pairing retained edges.
// @param edges The conveyance edges to organize into rows.
// @returns An array of EdgeRow objects for rendering.
export function buildEdgeRows(edges: ConveyanceEdge[]): EdgeRow[] {
  const consolidationGroups = new Map<string, ConveyanceEdge[]>();
  const nonConsolidation: ConveyanceEdge[] = [];

  for (const edge of edges) {
    if (edge.eventType === 'E5') {
      const key = edge.toNodeId;
      if (!consolidationGroups.has(key)) consolidationGroups.set(key, []);
      consolidationGroups.get(key)!.push(edge);
    } else {
      nonConsolidation.push(edge);
    }
  }

  // Bucket non-consolidation edges by from_node_id::event_type::transaction_id
  const buckets = new Map<string, ConveyanceEdge[]>();
  for (const edge of nonConsolidation) {
    const key = `${edge.fromNodeId}::${edge.eventType}::${
      edge.transactionId ?? 0
    }`;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(edge);
  }

  const rows: EdgeRow[] = [];
  const paired = new Set<string>();

  for (const [bucketKey, bucketEdges] of buckets) {
    const conveyed = bucketEdges.filter((e) => !e.params.retained);
    const retained = bucketEdges.filter((e) => e.params.retained);

    if (conveyed.length === 1 && retained.length === 1) {
      rows.push({
        kind: 'paired',
        conveyedEdge: conveyed[0],
        retainedEdge: retained[0],
        groupKey: `paired::${bucketKey}`,
      });
      paired.add(conveyed[0].id);
      paired.add(retained[0].id);
    }
  }

  // Emit unpaired non-consolidation edges as single rows (preserving original order)
  for (const edge of nonConsolidation) {
    if (!paired.has(edge.id)) {
      rows.push({ kind: 'single', edge });
    }
  }

  for (const [toNodeId, groupEdges] of consolidationGroups) {
    rows.push({
      kind: 'consolidation',
      edges: groupEdges,
      toNodeId,
      groupKey: `consolidation::${toNodeId}`,
    });
  }

  return rows;
}

// Count logical operations (consolidation group = 1, conveyed+retained pair = 1)
function countLogicalOps(edges: ConveyanceEdge[]): number {
  const consolidationTargets = new Set<string>();
  const nonConsolidation: ConveyanceEdge[] = [];

  for (const edge of edges) {
    if (edge.eventType === 'E5') {
      consolidationTargets.add(edge.toNodeId);
    } else {
      nonConsolidation.push(edge);
    }
  }

  // Bucket by from_node_id::event_type::transaction_id to find paired retained edges
  const buckets = new Map<string, ConveyanceEdge[]>();
  for (const edge of nonConsolidation) {
    const key = `${edge.fromNodeId}::${edge.eventType}::${
      edge.transactionId ?? 0
    }`;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(edge);
  }

  let count = 0;
  for (const bucketEdges of buckets.values()) {
    const conveyed = bucketEdges.filter((e) => !e.params.retained);
    const retained = bucketEdges.filter((e) => e.params.retained);
    if (conveyed.length === 1 && retained.length === 1) {
      count += 1; // pair counts as 1
    } else {
      count += bucketEdges.length;
    }
  }

  return count + consolidationTargets.size;
}

// =============================================================================
// Node Detail (BEFORE / AFTER column)
// =============================================================================

const TOTAL_COLUMNS = 2 + INTEREST_COLUMNS.length + 1; // Entity, Property + interests + NRI

function OwnershipNodeRow({
  node,
  label,
  isSelected,
  onNavigate,
  onAssetClick,
  onAssetLeave,
}: {
  node: OwnershipNode;
  label: string;
  isSelected: boolean;
  onNavigate?: (nodeId: string) => void;
  onAssetClick?: (data: {
    propertyName: string;
    nodeId: string;
    anchorRect?: DOMRect;
    pin?: boolean;
  }) => void;
  onAssetLeave?: () => void;
}) {
  const nri = getNodeNRI(node);
  const depthStr = formatDepthBand(node.property.volume.depthRange);
  const propertyTypeLabel =
    node.property.propertyType === 'tract'
      ? 'Tract'
      : node.property.propertyType === 'tract_volume'
        ? 'Tract Volume'
        : 'Well';

  return (
    <tr
      className={classNames(styles.odRow, {
        [styles.odRowSelected]: isSelected,
      })}
      onClick={(e) => {
        e.stopPropagation();
        onNavigate?.(node.id);
      }}
    >
      <td className={styles.odEntityCell}>
        <span className={styles.odEntityName}>{node.entity.entityName}</span>
        {label === 'RETAINED' && (
          <span className={styles.nodeRoleBadge}>{label}</span>
        )}
      </td>
      <td className={styles.odPropertyCell}>
        {onAssetClick ? (
          <span
            className={`${styles.odPropertyPrimary} ${tableStyles['property-link']}`}
            onMouseEnter={(e: React.MouseEvent) => {
              const rect = (
                e.currentTarget as HTMLElement
              ).getBoundingClientRect();
              onAssetClick({
                propertyName: node.property.propertyName,
                nodeId: node.id,
                anchorRect: rect,
              });
            }}
            onMouseLeave={onAssetLeave}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              const rect = (
                e.currentTarget as HTMLElement
              ).getBoundingClientRect();
              onAssetClick({
                propertyName: node.property.propertyName,
                nodeId: node.id,
                anchorRect: rect,
                pin: true,
              });
            }}
          >
            {node.property.propertyName}
          </span>
        ) : (
          <span className={styles.odPropertyPrimary}>
            {node.property.propertyName}
          </span>
        )}
        <span className={styles.odSecondaryText}>
          <span className={styles.odPropertyTag}>{propertyTypeLabel}</span>{' '}
          {depthStr}
        </span>
      </td>
      {INTEREST_COLUMNS.map((col) => {
        const fraction = getInterestFraction(node, col.key);
        return (
          <td key={col.key} className={styles.odInterestCell}>
            {fraction && <span className={styles.odFraction}>{fraction}</span>}
          </td>
        );
      })}
      <td className={styles.odInterestCell}>
        {nri !== null && (
          <span className={styles.odFraction}>{format2(nri * 100)}%</span>
        )}
      </td>
    </tr>
  );
}

export function OwnershipDetailTable({
  preNodes,
  postNodes,
  selectedNodeId,
  onNavigate,
  onAssetClick,
  onAssetLeave,
}: {
  preNodes: Array<{ node: OwnershipNode; label: string }>;
  postNodes: Array<{ node: OwnershipNode; label: string }>;
  selectedNodeId?: string | null;
  onNavigate?: (nodeId: string) => void;
  onAssetClick?: (data: {
    propertyName: string;
    nodeId: string;
    anchorRect?: DOMRect;
    pin?: boolean;
  }) => void;
  onAssetLeave?: () => void;
}) {
  return (
    <table className={styles.ownershipDetailTable}>
      <thead>
        <tr>
          <th className={styles.odEntityCol}>Entity</th>
          <th className={styles.odPropertyCol}>Property</th>
          {INTEREST_COLUMNS.map((col) => (
            <th key={col.key} className={styles.odInterestCol}>
              {col.label}
            </th>
          ))}
          <th className={styles.odInterestCol}>NRI</th>
        </tr>
      </thead>
      <tbody>
        <tr className={styles.odSectionRow}>
          <td colSpan={TOTAL_COLUMNS}>Pre-Operation Ownership</td>
        </tr>
        {preNodes.map(({ node, label }) => (
          <OwnershipNodeRow
            key={node.id}
            node={node}
            label={label}
            isSelected={selectedNodeId === node.id}
            onNavigate={onNavigate}
            onAssetClick={onAssetClick}
            onAssetLeave={onAssetLeave}
          />
        ))}
        <tr className={styles.odSectionRow}>
          <td colSpan={TOTAL_COLUMNS}>Post-Operation Ownership</td>
        </tr>
        {postNodes.map(({ node, label }) => (
          <OwnershipNodeRow
            key={node.id}
            node={node}
            label={label}
            isSelected={selectedNodeId === node.id}
            onNavigate={onNavigate}
            onAssetClick={onAssetClick}
            onAssetLeave={onAssetLeave}
          />
        ))}
      </tbody>
    </table>
  );
}

// =============================================================================
// Component
// =============================================================================

export function ConveyanceRunsheet({
  ownershipGraph,
  documents: documentsMap,
  documentDetails,
  totalDocumentCount,
  selectedEdgeIds,
  selectedDocumentId,
  selectedTransactionId,
  selectedNodeId,
  onEdgeClick,
  onNodeClick,
  onDocumentClick,
  onTransactionClick,
  onEnterDocReview,
  startDate,
  endDate,
  geographies,
  onAssetClick,
  onAssetLeave,
}: ConveyanceRunsheetProps) {
  const [expandedDocs, setExpandedDocs] = useState<Set<number>>(new Set());
  const [expandedEdgeIds, setExpandedEdgeIds] = useState<Set<string>>(
    new Set()
  );
  const [expandedOpsSections, setExpandedOpsSections] = useState<Set<number>>(
    new Set()
  );

  const toggleEdgeExpand = useCallback((key: string) => {
    setExpandedEdgeIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const [sortOrder, setSortOrder] = useState<SortOrder>('effective_newest');
  const docRowRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const edgeRowRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const setDocRowRef = useCallback(
    (docId: number, el: HTMLDivElement | null) => {
      if (el) {
        docRowRefs.current.set(docId, el);
      } else {
        docRowRefs.current.delete(docId);
      }
    },
    []
  );

  const setEdgeRowRef = useCallback(
    (edgeId: string, el: HTMLDivElement | null) => {
      if (el) {
        edgeRowRefs.current.set(edgeId, el);
      } else {
        edgeRowRefs.current.delete(edgeId);
      }
    },
    []
  );

  const documents = useMemo(
    () => groupEdgesByDocument(ownershipGraph, documentsMap, documentDetails),
    [ownershipGraph, documentsMap, documentDetails]
  );

  const sortedDocuments = useMemo(() => {
    const sorted = [...documents];
    sorted.sort((a, b) => {
      if (sortOrder === 'effective_newest')
        return b.effectiveDate.localeCompare(a.effectiveDate);
      if (sortOrder === 'effective_oldest')
        return a.effectiveDate.localeCompare(b.effectiveDate);
      const ra = a.recordingDate ?? '';
      const rb = b.recordingDate ?? '';
      if (sortOrder === 'recorded_newest') return rb.localeCompare(ra);
      return ra.localeCompare(rb);
    });
    return sorted;
  }, [documents, sortOrder]);

  const nodeMap = useMemo(() => {
    return ownershipGraph.nodes;
  }, [ownershipGraph.nodes]);

  // Resolve the document ID & title for the "Open in Document Review" button.
  // If a document is selected, use it directly. If a transaction is selected,
  // find which document contains it.
  const selectedDocIdForReview = useMemo(() => {
    if (selectedDocumentId !== null) return selectedDocumentId;
    if (selectedTransactionId !== null) {
      const doc = sortedDocuments.find((d) =>
        d.transactions.some((t) => t.transactionId === selectedTransactionId)
      );
      return doc?.documentId ?? null;
    }
    return null;
  }, [selectedDocumentId, selectedTransactionId, sortedDocuments]);

  const selectedDocTitle = useMemo(() => {
    if (selectedDocIdForReview === null) return null;
    const rec = documentsMap[String(selectedDocIdForReview)];
    return rec?.documentTitle ?? null;
  }, [selectedDocIdForReview, documentsMap]);

  const toggleDoc = useCallback((docId: number) => {
    setExpandedDocs((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedDocs(new Set(documents.map((d) => d.documentId)));
    // Also expand all edge rows
    const allEdgeKeys: string[] = [];
    for (const edge of Object.values(ownershipGraph.edges)) {
      allEdgeKeys.push(edge.id);
    }
    // Add paired and consolidation group keys
    const allEdges = Object.values(ownershipGraph.edges);
    for (const row of buildEdgeRows(allEdges)) {
      if (row.kind === 'paired') allEdgeKeys.push(row.groupKey);
      else if (row.kind === 'consolidation') allEdgeKeys.push(row.groupKey);
    }
    setExpandedEdgeIds(new Set(allEdgeKeys));
  }, [documents, ownershipGraph.edges]);

  const collapseAll = useCallback(() => {
    setExpandedDocs(new Set());
    setExpandedEdgeIds(new Set());
  }, []);

  // Auto-expand and scroll to selected document
  useEffect(() => {
    if (selectedDocumentId === null) return;

    setExpandedDocs((prev) => {
      if (prev.has(selectedDocumentId)) return prev;
      const next = new Set(prev);
      next.add(selectedDocumentId);
      return next;
    });

    requestAnimationFrame(() => {
      const el = docRowRefs.current.get(selectedDocumentId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [selectedDocumentId]);

  // Auto-expand parent document when a transaction is selected (e.g. from graph click)
  useEffect(() => {
    if (selectedTransactionId === null) return;

    const parentDoc = documents.find((d) =>
      d.transactions.some((t) => t.transactionId === selectedTransactionId)
    );
    if (!parentDoc) return;

    setExpandedDocs((prev) => {
      if (prev.has(parentDoc.documentId)) return prev;
      const next = new Set(prev);
      next.add(parentDoc.documentId);
      return next;
    });

    requestAnimationFrame(() => {
      const el = docRowRefs.current.get(parentDoc.documentId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [selectedTransactionId, documents]);

  // Auto-expand documents/transactions containing selected edges and scroll to first
  useEffect(() => {
    if (selectedEdgeIds.size === 0) return;

    const docsToExpand = new Set<number>();
    const txnsToExpand = new Set<number>();
    let firstEdgeId: string | null = null;

    for (const doc of documents) {
      for (const txn of doc.transactions) {
        for (const edge of txn.edges) {
          if (selectedEdgeIds.has(edge.id)) {
            docsToExpand.add(doc.documentId);
            if (txn.transactionId) txnsToExpand.add(txn.transactionId);
            if (!firstEdgeId) firstEdgeId = edge.id;
          }
        }
      }
    }

    if (docsToExpand.size === 0) return;

    // Expand parent documents
    setExpandedDocs((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of docsToExpand) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    // Expand operations sections for transactions containing selected edges
    setExpandedOpsSections((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of txnsToExpand) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    // Scroll to the first selected edge row (wait for DOM to update after expand)
    if (firstEdgeId) {
      const scrollTarget = firstEdgeId;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const el = edgeRowRefs.current.get(scrollTarget);
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          });
        });
      });
    }
  }, [selectedEdgeIds, documents]);

  // Shared edge-rows renderer — used by both document view and transaction view
  function renderEdgeRows(edges: ConveyanceEdge[], documentId: number) {
    return buildEdgeRows(edges).map((row) => {
      if (row.kind === 'single') {
        const { edge } = row;
        const fromNode =
          edge.fromNodeId !== null ? nodeMap[edge.fromNodeId] : undefined;
        const toNode =
          edge.toNodeId !== null ? nodeMap[edge.toNodeId] : undefined;
        const isSelected = selectedEdgeIds.has(edge.id);
        const isRowExpanded = expandedEdgeIds.has(edge.id);
        const detailText = getEdgeTypeLabel(
          edge.eventType,
          edge.params.retained,
          fromNode,
          toNode
        );
        const isNullNodeEdge =
          edge.fromNodeId === null || edge.toNodeId === null;

        // Invalid edge with null node IDs — show conveyor/conveyee from
        // interpretation data, validation reason + Invalid badge in DETAIL column
        if (isNullNodeEdge) {
          // Look up conveyor/conveyee from interpretation transaction data
          const docDetail = documentDetails?.[String(documentId)];
          let conveyorName: string | null = null;
          let conveyorId: number | null = null;
          let conveyeeName: string | null = null;
          let conveyeeId: number | null = null;
          if (docDetail) {
            for (const interp of docDetail.interpretations) {
              const tx = (interp.transactions ?? []).find(
                (t) => t.id === edge.transactionId
              );
              if (tx) {
                if (tx.grantors.length > 0) {
                  conveyorName = tx.grantors[0].name;
                  conveyorId = tx.grantors[0].entityId ?? null;
                }
                if (tx.conveyances.length > 0) {
                  conveyeeName = tx.conveyances[0].grantee_name;
                  conveyeeId = tx.conveyances[0].grantee_id;
                }
                break;
              }
            }
          }
          return (
            <div key={edge.id}>
              <div
                ref={(el) => setEdgeRowRef(edge.id, el)}
                className={classNames(styles.edgeRow, styles.edgeRowInvalid, {
                  [styles.selected]: isSelected,
                })}
                onClick={() => onEdgeClick(edge.id, documentId)}
              >
                <span className={styles.edgeExpandToggle} />
                <span className={`${styles.edgeCell} ${styles.edgeCellFixed}`}>
                  <span className={styles.edgeTypeText}>{detailText}</span>
                  <span className={styles.invalidBadge}>Invalid</span>
                </span>
                <span
                  className={`${styles.edgeCell} ${styles.edgeEntityLink} ${styles.edgeCellFlex}`}
                >
                  {conveyorName && conveyorId ? (
                    <EntityLink entityId={conveyorId}>
                      {conveyorName}
                    </EntityLink>
                  ) : null}
                </span>
                <span
                  className={`${styles.edgeCell} ${styles.edgeEntityLink} ${styles.edgeCellFlex}`}
                >
                  {conveyeeName && conveyeeId ? (
                    <EntityLink entityId={conveyeeId}>
                      {conveyeeName}
                    </EntityLink>
                  ) : null}
                </span>
                <span className={`${styles.edgeCell} ${styles.edgeCellFlex}`}>
                  <WarningHex className={styles.invalidIcon} />
                  <span className={styles.invalidReasonText}>
                    {edge.validationReason || 'Invalid operation'}
                  </span>
                </span>
              </div>
            </div>
          );
        }

        return (
          <div key={edge.id}>
            <div
              ref={(el) => setEdgeRowRef(edge.id, el)}
              className={classNames(styles.edgeRow, {
                [styles.selected]: isSelected,
              })}
              onClick={() => onEdgeClick(edge.id, documentId)}
            >
              <button
                type="button"
                className={styles.edgeExpandToggle}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleEdgeExpand(edge.id);
                }}
                title={
                  isRowExpanded
                    ? EDGE_DETAIL_COLLAPSE_LABEL
                    : EDGE_DETAIL_EXPAND_LABEL
                }
                aria-label={
                  isRowExpanded
                    ? EDGE_DETAIL_COLLAPSE_LABEL
                    : EDGE_DETAIL_EXPAND_LABEL
                }
                aria-expanded={isRowExpanded}
              >
                {isRowExpanded ? <ChevronDown /> : <ChevronRight />}
              </button>
              <span className={`${styles.edgeCell} ${styles.edgeCellFixed}`}>
                <span className={styles.edgeTypeText}>
                  {getEdgeTypeName(edge.eventType)}
                </span>
              </span>
              <span
                className={`${styles.edgeCell} ${styles.edgeEntityLink} ${styles.edgeCellFlex}`}
              >
                {fromNode ? (
                  <EntityLink entityId={fromNode.entity.entityId}>
                    {fromNode.entity.entityName}
                  </EntityLink>
                ) : (
                  edge.fromNodeId
                )}
              </span>
              <span
                className={`${styles.edgeCell} ${styles.edgeEntityLink} ${styles.edgeCellFlex}`}
              >
                {toNode ? (
                  <EntityLink entityId={toNode.entity.entityId}>
                    {toNode.entity.entityName}
                  </EntityLink>
                ) : (
                  edge.toNodeId
                )}
              </span>
              <span
                className={`${styles.edgeCell} ${styles.detailCell} ${styles.edgeCellFlex}`}
              >
                <span className={styles.detailLine1}>
                  <span className={styles.quantumFunction}>{detailText}</span>
                  {edge.params.retained && (
                    <span className={styles.retainedBadge}>Retained</span>
                  )}
                </span>
                {(() => {
                  const expr = computeOperationExpression(edge, nodeMap);
                  return expr ? (
                    <span className={styles.operationExpression}>{expr}</span>
                  ) : null;
                })()}
              </span>
            </div>
            {isRowExpanded && fromNode && toNode && (
              <div className={styles.consolidationDetail}>
                <OwnershipDetailTable
                  preNodes={[{ node: fromNode, label: '' }]}
                  postNodes={[{ node: toNode, label: '' }]}
                  selectedNodeId={selectedNodeId}
                  onNavigate={(id) => onNodeClick?.(id)}
                  onAssetClick={onAssetClick}
                  onAssetLeave={onAssetLeave}
                />
              </div>
            )}
          </div>
        );
      }

      if (row.kind === 'paired') {
        const { conveyedEdge, retainedEdge, groupKey } = row;
        const fromNode = conveyedEdge.fromNodeId
          ? nodeMap[conveyedEdge.fromNodeId]
          : undefined;
        const conveyedToNode = nodeMap[conveyedEdge.toNodeId];
        const retainedToNode = nodeMap[retainedEdge.toNodeId];
        const isPairSelected =
          selectedEdgeIds.has(conveyedEdge.id) ||
          selectedEdgeIds.has(retainedEdge.id);
        const isPairExpanded = expandedEdgeIds.has(groupKey);
        const detailText = getEdgeTypeLabel(
          conveyedEdge.eventType,
          false,
          fromNode,
          conveyedToNode
        );
        return (
          <div key={groupKey}>
            <div
              ref={(el) => {
                setEdgeRowRef(conveyedEdge.id, el);
                setEdgeRowRef(retainedEdge.id, el);
              }}
              className={classNames(styles.edgeRow, {
                [styles.selected]: isPairSelected,
              })}
              onClick={() => onEdgeClick(conveyedEdge.id, documentId)}
            >
              <button
                type="button"
                className={styles.edgeExpandToggle}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleEdgeExpand(groupKey);
                }}
                title={
                  isPairExpanded
                    ? EDGE_DETAIL_COLLAPSE_LABEL
                    : EDGE_DETAIL_EXPAND_LABEL
                }
                aria-label={
                  isPairExpanded
                    ? EDGE_DETAIL_COLLAPSE_LABEL
                    : EDGE_DETAIL_EXPAND_LABEL
                }
                aria-expanded={isPairExpanded}
              >
                {isPairExpanded ? <ChevronDown /> : <ChevronRight />}
              </button>
              <span className={`${styles.edgeCell} ${styles.edgeCellFixed}`}>
                <span className={styles.edgeTypeText}>
                  {getEdgeTypeName(conveyedEdge.eventType)}
                </span>
              </span>
              <span
                className={`${styles.edgeCell} ${styles.edgeEntityLink} ${styles.edgeCellFlex}`}
              >
                {fromNode ? (
                  <EntityLink entityId={fromNode.entity.entityId}>
                    {fromNode.entity.entityName}
                  </EntityLink>
                ) : (
                  conveyedEdge.fromNodeId
                )}
              </span>
              <span
                className={`${styles.edgeCell} ${styles.edgeEntityLink} ${styles.edgeCellFlex}`}
              >
                {conveyedToNode ? (
                  <EntityLink entityId={conveyedToNode.entity.entityId}>
                    {conveyedToNode.entity.entityName}
                  </EntityLink>
                ) : (
                  conveyedEdge.toNodeId
                )}
              </span>
              <span
                className={`${styles.edgeCell} ${styles.detailCell} ${styles.edgeCellFlex}`}
              >
                <span className={styles.detailLine1}>
                  <span className={styles.quantumFunction}>{detailText}</span>
                </span>
                {(() => {
                  const expr = computeOperationExpression(
                    conveyedEdge,
                    nodeMap
                  );
                  return expr ? (
                    <span className={styles.operationExpression}>{expr}</span>
                  ) : null;
                })()}
              </span>
            </div>
            {isPairExpanded && fromNode && conveyedToNode && retainedToNode && (
              <div className={styles.consolidationDetail}>
                <OwnershipDetailTable
                  preNodes={[{ node: fromNode, label: '' }]}
                  postNodes={[
                    { node: conveyedToNode, label: 'CONVEYED' },
                    { node: retainedToNode, label: 'RETAINED' },
                  ]}
                  selectedNodeId={selectedNodeId}
                  onNavigate={(id) => onNodeClick?.(id)}
                  onAssetClick={onAssetClick}
                  onAssetLeave={onAssetLeave}
                />
                {conveyedEdge.eventType === 'E2' && (
                  <div className={styles.severanceVizWrapper}>
                    <SeveranceVisualization
                      sourceNode={fromNode}
                      outputNodes={[conveyedToNode, retainedToNode]}
                      geographies={geographies}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        );
      }

      // Consolidation group
      const toNode = nodeMap[row.toNodeId];
      const isGroupSelected = row.edges.some((e) => selectedEdgeIds.has(e.id));
      const isGroupExpanded = expandedEdgeIds.has(row.groupKey);
      return (
        <div key={row.groupKey}>
          <div
            ref={(el) => {
              for (const e of row.edges) setEdgeRowRef(e.id, el);
            }}
            className={classNames(styles.edgeRow, {
              [styles.selected]: isGroupSelected,
            })}
            onClick={() => onEdgeClick(row.edges[0].id, documentId)}
          >
            <button
              type="button"
              className={styles.edgeExpandToggle}
              onClick={(e) => {
                e.stopPropagation();
                toggleEdgeExpand(row.groupKey);
              }}
              title={
                isGroupExpanded
                  ? EDGE_DETAIL_COLLAPSE_LABEL
                  : EDGE_DETAIL_EXPAND_LABEL
              }
              aria-label={
                isGroupExpanded
                  ? EDGE_DETAIL_COLLAPSE_LABEL
                  : EDGE_DETAIL_EXPAND_LABEL
              }
              aria-expanded={isGroupExpanded}
            >
              {isGroupExpanded ? <ChevronDown /> : <ChevronRight />}
            </button>
            <span className={`${styles.edgeCell} ${styles.edgeCellFixed}`}>
              <span className={styles.edgeTypeText}>Consolidation</span>
            </span>
            <span
              className={`${styles.edgeCell} ${styles.edgeEntityLink} ${styles.edgeCellFlex}`}
            >
              {row.edges.length} conveyor{row.edges.length !== 1 ? 's' : ''}
            </span>
            <span
              className={`${styles.edgeCell} ${styles.edgeEntityLink} ${styles.edgeCellFlex}`}
            >
              {toNode ? (
                <EntityLink entityId={toNode.entity.entityId}>
                  {toNode.entity.entityName}
                </EntityLink>
              ) : (
                row.toNodeId
              )}
            </span>
            <span className={`${styles.edgeCell} ${styles.edgeCellFlex}`}>
              <span className={styles.quantumFunction}>
                Merge: &Sigma;(inputs)
              </span>
            </span>
          </div>
          {isGroupExpanded && toNode && (
            <div className={styles.consolidationDetail}>
              <OwnershipDetailTable
                preNodes={row.edges
                  .map((edge) => {
                    const fromNode = edge.fromNodeId
                      ? nodeMap[edge.fromNodeId]
                      : undefined;
                    return fromNode ? { node: fromNode, label: '' } : null;
                  })
                  .filter(
                    (n): n is { node: OwnershipNode; label: string } =>
                      n !== null
                  )}
                postNodes={[{ node: toNode, label: '' }]}
                selectedNodeId={selectedNodeId}
                onNavigate={(id) => onNodeClick?.(id)}
                onAssetClick={onAssetClick}
                onAssetLeave={onAssetLeave}
              />
            </div>
          )}
        </div>
      );
    });
  }

  if (Object.keys(ownershipGraph.edges).length === 0) {
    return (
      <div className={styles.runsheetContainer}>
        <div className={styles.runsheetHeader}>
          <div className={styles.headerLeft}>
            <span className={styles.headerTitle}>Runsheet</span>
          </div>
        </div>
        <div className={styles.emptyState}>
          <Document />
          <p>No documents in the current timeframe</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.runsheetContainer}>
      {/* Header */}
      <div className={styles.runsheetHeader}>
        <div className={styles.headerLeft}>
          <span className={styles.headerTitle}>Runsheet</span>
          {(startDate || endDate) && (
            <span className={styles.headerDateRange}>
              {startDate && endDate ? (
                <>
                  From {formatShortDate(startDate)}
                  {' \u2014 '}
                  {formatShortDate(endDate)}
                </>
              ) : startDate ? (
                <>From {formatShortDate(startDate)}</>
              ) : (
                <>As of {formatShortDate(endDate!)}</>
              )}
            </span>
          )}
          <span className={styles.headerCount}>
            {sortedDocuments.length}
            {totalDocumentCount && totalDocumentCount !== sortedDocuments.length
              ? ` of ${totalDocumentCount}`
              : ''}{' '}
            documents
          </span>
        </div>
        <div className={styles.headerRight}>
          {onEnterDocReview && (
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={DocumentMultiple01}
              iconDescription={
                selectedDocTitle
                  ? `Open ${selectedDocTitle} in Document Review`
                  : 'Open Document Review'
              }
              tooltipPosition="bottom"
              onClick={() =>
                onEnterDocReview(selectedDocIdForReview ?? undefined)
              }
            />
          )}
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={ExpandAll}
            iconDescription="Expand all"
            tooltipPosition="bottom"
            onClick={expandAll}
          />
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={CollapseAll}
            iconDescription="Collapse all"
            tooltipPosition="bottom"
            onClick={collapseAll}
          />
          <div
            className={styles.sortDropdown}
            onClick={(e) => e.stopPropagation()}
          >
            <Dropdown
              id="runsheet-sort"
              size="sm"
              titleText=""
              label="Sort"
              hideLabel
              itemToString={(item: { id: string; text: string }) =>
                item?.text ?? ''
              }
              items={[...SORT_OPTIONS]}
              selectedItem={
                SORT_OPTIONS.find((o) => o.id === sortOrder) ?? SORT_OPTIONS[0]
              }
              onChange={({
                selectedItem,
              }: {
                selectedItem: { id: string; text: string };
              }) => {
                if (selectedItem) setSortOrder(selectedItem.id as SortOrder);
              }}
            />
          </div>
        </div>
      </div>
      {/* Document List */}
      <div className={styles.documentList}>
        {sortedDocuments.map((doc) => {
          const isExpanded = expandedDocs.has(doc.documentId);
          const isDocSelected = selectedDocumentId === doc.documentId;

          return (
            <div
              key={doc.documentId}
              ref={(el) => setDocRowRef(doc.documentId, el)}
              className={classNames(styles.txFirstRow, {
                [styles.selectedDocument]: isDocSelected,
              })}
            >
              {/* 5-column grid: Expand | Doc Info | Areas | Dates | Parties */}
              <div className={styles.documentHeader}>
                <button
                  type="button"
                  className={styles.expandToggle}
                  onClick={() => toggleDoc(doc.documentId)}
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                >
                  {isExpanded ? <ChevronDown /> : <ChevronRight />}
                </button>

                <div
                  className={classNames(styles.docTopRow, {
                    [styles.clickableDocRow]: !!onDocumentClick,
                  })}
                  onClick={
                    onDocumentClick
                      ? () => onDocumentClick(doc.documentId)
                      : undefined
                  }
                >
                  {/* Col 1: Document info + transaction count */}
                  <div className={styles.docInfoCol}>
                    <span className={styles.documentTitle}>
                      <LinkWithHover
                        kind="document"
                        href={`/documents/${doc.documentId}`}
                      >
                        <a
                          href={`/documents/${doc.documentId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {doc.documentTitle ?? `Document #${doc.documentId}`}
                        </a>
                      </LinkWithHover>
                    </span>
                    {(doc.documentType || doc.documentTitle) && (
                      <span className={styles.documentSubtitle}>
                        {doc.documentType ?? `Document #${doc.documentId}`}
                        {doc.county &&
                          ` \u00B7 ${doc.county}${
                            doc.state ? `, ${doc.state}` : ''
                          }`}
                      </span>
                    )}
                    <span className={styles.docAreaText}>
                      {doc.transactions.length} transaction
                      {doc.transactions.length !== 1 ? 's' : ''}
                      {doc.totalOps > 0 &&
                        ` · ${doc.totalOps} operation${
                          doc.totalOps !== 1 ? 's' : ''
                        }`}
                    </span>
                    {doc.invalidCount > 0 && (
                      <span className={styles.docInvalidTag}>
                        <WarningHex className={styles.invalidBadgeIcon} />
                        {doc.invalidCount} Operation
                        {doc.invalidCount !== 1 ? 's' : ''} Invalid
                      </span>
                    )}
                  </div>

                  {/* Col 2: Applicable areas */}
                  <div className={styles.txInfoCol}>
                    {doc.areas.map((area) => (
                      <span
                        key={`${area.name}::${area.depth ?? ''}`}
                        className={styles.docAreaText}
                      >
                        {area.name}
                        {area.depth && (
                          <>
                            {' '}
                            <span className={styles.docAreaDepth}>
                              {area.depth}
                            </span>
                          </>
                        )}
                      </span>
                    ))}
                  </div>

                  {/* Col 3: Dates */}
                  <div className={styles.docDatesCol}>
                    {doc.transactionDate && (
                      <div className={styles.dateRow}>
                        <Calendar className={styles.dateIcon} />
                        <span className={styles.dateLabel}>
                          Transaction Date
                        </span>
                        <span className={styles.dateValue}>
                          {formatDate(doc.transactionDate)}
                        </span>
                      </div>
                    )}
                    {doc.effectiveDate && (
                      <div className={styles.dateRow}>
                        <EventSchedule className={styles.dateIcon} />
                        <span className={styles.dateLabel}>Effective Date</span>
                        <span className={styles.dateValue}>
                          {formatDate(doc.effectiveDate)}
                        </span>
                      </div>
                    )}
                    {doc.instrumentDate && (
                      <div className={styles.dateRow}>
                        <Document className={styles.dateIcon} />
                        <span className={styles.dateLabel}>
                          Instrument Date
                        </span>
                        <span className={styles.dateValue}>
                          {formatDate(doc.instrumentDate)}
                        </span>
                      </div>
                    )}
                    {doc.fileDate && (
                      <div className={styles.dateRow}>
                        <Catalog className={styles.dateIcon} />
                        <span className={styles.dateLabel}>File Date</span>
                        <span className={styles.dateValue}>
                          {formatDate(doc.fileDate)}
                        </span>
                      </div>
                    )}
                    {doc.recordingDate && (
                      <div className={styles.dateRow}>
                        <Time className={styles.dateIcon} />
                        <span className={styles.dateLabel}>Recorded Date</span>
                        <span className={styles.dateValue}>
                          {formatDate(doc.recordingDate)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Parties row — sits beside top row when wide, wraps below when narrow */}
                <div className={styles.docPartiesCol}>
                  {doc.grantors.length > 0 && (
                    <>
                      <span className={styles.partiesLabel}>
                        Conveyor{doc.grantors.length > 1 ? 's' : ''}
                        {doc.grantors.length > 1 && (
                          <span className={styles.partyCount}>
                            ({doc.grantors.length})
                          </span>
                        )}
                      </span>
                      <span className={styles.partiesValue}>
                        {doc.grantors.map((party, i) => (
                          <span key={party.name}>
                            <EntityLink entityId={party.entityId}>
                              {party.name}
                            </EntityLink>
                            {i < doc.grantors.length - 1 && ', '}
                          </span>
                        ))}
                      </span>
                    </>
                  )}
                  {doc.grantees.length > 0 && (
                    <>
                      <span className={styles.partiesLabel}>
                        Conveyee{doc.grantees.length > 1 ? 's' : ''}
                        {doc.grantees.length > 1 && (
                          <span className={styles.partyCount}>
                            ({doc.grantees.length})
                          </span>
                        )}
                      </span>
                      <span className={styles.partiesValue}>
                        {doc.grantees.map((party, i) => (
                          <span key={party.name}>
                            <EntityLink entityId={party.entityId}>
                              {party.name}
                            </EntityLink>
                            {i < doc.grantees.length - 1 && ', '}
                          </span>
                        ))}
                      </span>
                    </>
                  )}
                </div>
              </div>
              {/* Expanded: Transaction sub-rows (always shown, not individually collapsible) */}
              {isExpanded && (
                <div>
                  {doc.transactions.map((txn) => {
                    const txnOps = countLogicalOps(txn.edges);

                    return (
                      <div
                        key={txn.transactionId}
                        className={classNames(styles.transactionSection, {
                          [styles.selectedTransaction]:
                            selectedTransactionId === txn.transactionId,
                        })}
                        onClick={(e) => {
                          const target = e.target as HTMLElement;
                          if (target.closest(`.${styles.edgeRow}`)) return;
                          onTransactionClick?.(
                            selectedTransactionId === txn.transactionId
                              ? null
                              : txn.transactionId
                          );
                        }}
                      >
                        {/* Transaction header — single flow layout */}
                        <div className={styles.txSectionHeader}>
                          <div className={styles.txHeaderContent}>
                            <div className={styles.txTopRow}>
                              <span className={styles.txLabel}>
                                Transaction #{txn.transactionId}
                              </span>
                              <span className={styles.txSep}>&middot;</span>
                              {txn.effectiveDate && (
                                <span className={styles.txMeta}>
                                  {formatDate(txn.effectiveDate)}
                                </span>
                              )}
                              <span className={styles.txSep}>&middot;</span>
                              <span className={styles.txMeta}>
                                {txnOps} operation
                                {txnOps !== 1 ? 's' : ''}
                                {txn.conveyedEstates.length > 0 &&
                                  ` · ${txn.conveyedEstates.join(', ')}`}
                              </span>
                            </div>
                            <div className={styles.txBodyColumns}>
                              {txn.conveyors.length > 0 && (
                                <div className={styles.txPartyLine}>
                                  <span className={styles.partyRoleLabel}>
                                    Conveyors
                                  </span>
                                  <span className={styles.partyNames}>
                                    {txn.conveyors.map((party, i) => (
                                      <span key={party.name}>
                                        <EntityLink entityId={party.entityId}>
                                          {party.name}
                                        </EntityLink>
                                        {i < txn.conveyors.length - 1 && ', '}
                                      </span>
                                    ))}
                                  </span>
                                </div>
                              )}
                              {txn.conveyees.length > 0 && (
                                <div className={styles.txPartyLine}>
                                  <span className={styles.partyRoleLabel}>
                                    Conveyees
                                  </span>
                                  <span className={styles.partyNames}>
                                    {txn.conveyees.map((party, i) => (
                                      <span key={party.name}>
                                        <EntityLink entityId={party.entityId}>
                                          {party.name}
                                        </EntityLink>
                                        {i < txn.conveyees.length - 1 && ', '}
                                      </span>
                                    ))}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Operations table — collapsible */}
                        <div className={styles.opsCollapsible}>
                          <div className={styles.opsCollapsibleToggle}>
                            <button
                              type="button"
                              className={styles.opsCollapsibleToggleBtn}
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedOpsSections((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(txn.transactionId))
                                    next.delete(txn.transactionId);
                                  else next.add(txn.transactionId);
                                  return next;
                                });
                              }}
                            >
                              {expandedOpsSections.has(txn.transactionId) ? (
                                <ChevronDown />
                              ) : (
                                <ChevronRight />
                              )}
                              <span className={styles.opsCollapsibleLabel}>
                                Operations
                              </span>
                              <span className={styles.opsCollapsibleMeta}>
                                {txn.edges.length}{' '}
                                {txn.edges.length === 1
                                  ? 'operation'
                                  : 'operations'}
                              </span>
                            </button>
                            <div className={styles.opsCollapsibleActions}>
                              <FloatingTooltip
                                content="Expand all"
                                placement="left"
                              >
                                <button
                                  type="button"
                                  className={styles.opsActionBtn}
                                  onClick={(e2) => {
                                    e2.stopPropagation();
                                    const rows = buildEdgeRows(txn.edges);
                                    setExpandedEdgeIds((prev) => {
                                      const next = new Set(prev);
                                      for (const row of rows) {
                                        if (row.kind === 'single')
                                          next.add(row.edge.id);
                                        else if (row.kind === 'paired')
                                          next.add(row.groupKey);
                                        else if (row.kind === 'consolidation')
                                          next.add(row.groupKey);
                                      }
                                      return next;
                                    });
                                  }}
                                >
                                  <ExpandAll />
                                </button>
                              </FloatingTooltip>
                              <FloatingTooltip
                                content="Collapse all"
                                placement="left"
                              >
                                <button
                                  type="button"
                                  className={styles.opsActionBtn}
                                  onClick={(e2) => {
                                    e2.stopPropagation();
                                    const rows = buildEdgeRows(txn.edges);
                                    setExpandedEdgeIds((prev) => {
                                      const next = new Set(prev);
                                      for (const row of rows) {
                                        if (row.kind === 'single')
                                          next.delete(row.edge.id);
                                        else if (row.kind === 'paired')
                                          next.delete(row.groupKey);
                                        else if (row.kind === 'consolidation')
                                          next.delete(row.groupKey);
                                      }
                                      return next;
                                    });
                                  }}
                                >
                                  <CollapseAll />
                                </button>
                              </FloatingTooltip>
                            </div>
                          </div>
                          {expandedOpsSections.has(txn.transactionId) && (
                            <div className={styles.txSectionBody}>
                              <div className={styles.edgesHeader}>
                                <span
                                  className={`${styles.headerCell} ${styles.edgeCellFixed}`}
                                >
                                  Operation
                                </span>
                                <span
                                  className={`${styles.headerCell} ${styles.edgeCellFlex}`}
                                >
                                  Conveyor
                                </span>
                                <span
                                  className={`${styles.headerCell} ${styles.edgeCellFlex}`}
                                >
                                  Conveyee
                                </span>
                                <span
                                  className={`${styles.headerCell} ${styles.edgeCellFlex}`}
                                >
                                  Detail
                                </span>
                              </div>
                              {renderEdgeRows(txn.edges, doc.documentId)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
